import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as opsApi from "@/features/ops/api";
import { REPORT_KINDS } from "@/features/ops/types";

// Which reports accept a date range (e-signature/e-services style).
const DATED = new Set(["esignature"]);

export default function OpsReportsPage() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const download = async (kind: string) => {
    setBusy(kind);
    try {
      await opsApi.downloadReport(kind, DATED.has(kind) ? { from, to } : {});
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Report failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Date range (for dated reports)</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="from">From</Label>
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="to">To</Label>
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        {REPORT_KINDS.map((r) => (
          <Card key={r.key}>
            <CardHeader>
              <CardTitle className="text-base">{r.label}</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">
                {DATED.has(r.key) ? "Uses the date range above" : "Full export"}
              </span>
              <Button size="sm" disabled={busy === r.key} onClick={() => download(r.key)}>
                {busy === r.key ? "Preparing…" : "Download .xlsx"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-muted-foreground text-xs">
        Member discrepancies (WordPress) and Publish help nodes are out of scope for this app (spec
        §12). CPA Charge and transcript-discrepancy rows populate as their tier-owned data sources
        come online.
      </p>
    </div>
  );
}
